import React, { useState, useEffect } from "react";
import { Menu, X, PawPrint } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="22146" className="flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded-none" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-[14px] font-sans font-medium uppercase tracking-[0.1em] text-[#3b3b3b] transition-all duration-300 hover:text-[#c4a670] hover:underline hover:decoration-[#c4a670] hover:underline-offset-8">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onItemClick
}: {
  onItemClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted/20 animate-pulse rounded-none" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-6">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-sans font-medium uppercase tracking-[0.1em] text-[#3b3b3b] hover:text-[#c4a670] transition-colors" onClick={onItemClick}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function KennelEliteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${isScrolled ? "bg-[#fdfaf7]/80 backdrop-blur-md border-b border-[#e5e7eb] shadow-sm" : "bg-[#fdfaf7] border-b border-transparent"}`}>
      <div className="container mx-auto px-4 min-h-20 flex flex-wrap items-center justify-between py-2 md:py-0">
        {}
        <Link to="/" className="flex-shrink-0 z-50">
          <img src="https://milehighcavaliers.10web.cloud/wp-content/uploads/2026/03/mile_high_cavaliers_logo_black_large.png" alt="Mile High Cavaliers" className="object-contain h-12 !w-[400px] md:h-20" />
        </Link>

        {}
        <div className="hidden md:flex items-center justify-center flex-1 px-8">
          <MenuProvider menu_id="50" className="w-full flex justify-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex items-center gap-4">
          <Button variant="outline" asChild className="border-[#c4a670] text-[#c4a670] hover:bg-[#c4a670] hover:text-white transition-all rounded-none uppercase text-[12px] tracking-widest font-sans">
            
          </Button>
          
          <div className="relative group">
            <PawPrint className="text-[#3b3b3b] hover:text-[#c4a670] transition-colors size-6" />
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-[#c4a670] rounded-full blur-[1px] opacity-80 animate-pulse pointer-events-none" />
          </div>
        </div>

        {}
        <div className="flex items-center gap-4 md:hidden">
          <PawPrint className="text-[#3b3b3b] hover:text-[#c4a670] size-6" />
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-[#3b3b3b] hover:text-[#c4a670] size-10" aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="md:hidden absolute top-20 left-0 w-full bg-[#fdfaf7]/95 backdrop-blur-xl border-b border-[#e5e7eb] shadow-xl animate-in slide-in-from-top-5 duration-200">
          <div className="flex flex-col p-4 pb-8">
            <MenuProvider menu_id="50">
              <MobileMenuConsumer onItemClick={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
            
            <div className="px-6 mt-4">
              <Button variant="outline" asChild className="w-full border-[#c4a670] text-[#c4a670] hover:bg-[#c4a670] hover:text-white rounded-none uppercase text-[14px] tracking-widest">
                <Link to="#" onClick={() => setIsMobileMenuOpen(false)}>
                  Our Puppies
                </Link>
              </Button>
            </div>
          </div>
        </div>}
    </header>;
}